import { useSyncExternalStore } from "react";
import {
  cancelBulkJob,
  dismissBulkJob,
  getBulkJob,
  listActiveBulkJobs,
  retryFailedBulkJob,
} from "../../../api/blankProducts";
import {
  cancelNeckLabelJob,
  dismissNeckLabelJob,
  getNeckLabelJob,
  listActiveNeckLabelJobs,
  retryFailedNeckLabelJob,
} from "../../../api/neckLabels";
import {
  cancelDstReplaceJob,
  dismissDstReplaceJob,
  getDstReplaceJob,
  listActiveDstReplaceJobs,
  retryFailedDstReplaceJob,
} from "../../../api/dstLibrary";
import {
  cancelPlacementJob,
  dismissPlacementJob,
  getPlacementJob,
  listActivePlacementJobs,
  retryPlacementJob,
  undoPlacementJob,
} from "../../../api/placementDuplicate";

/**
 * Client state for server-side bulk jobs: Assign print methods (admins) and
 * neck labels on products / listings (`source: "neck_labels"`, everyone).
 *
 * The server is the source of truth: on load the tracker asks for every job
 * that isn't dismissed, so a refresh, a new tab or a sign-in on another device
 * picks running jobs back up. This store only holds the latest copy of each
 * job and polls the running ones.
 */

export const RUNNING_STATUSES = new Set(["queued", "running"]);
export const isJobRunning = (job) => RUNNING_STATUSES.has(job?.status);

const POLL_MS = 2000;
const POLL_BACKOFF_MS = 6000;
const MINIMIZED_KEY = "riverr.bulkJobsTracker.minimized";

let state = {
  jobs: [], // newest first
  loaded: false,
  minimized: readMinimized(),
};
const listeners = new Set();
const finishListeners = new Set();
let pollTimer = null;
let errorStreak = 0;
// Bumped by stop() and account switches so an in-flight poll can't restart
// polling or write the previous account's jobs.
let generation = 0;
let currentUid = null;

function readMinimized() {
  try {
    return window.sessionStorage.getItem(MINIMIZED_KEY) === "1";
  } catch (_) {
    return false;
  }
}

const emit = () => listeners.forEach((fn) => fn());

// Each job's API comes from where it was started. Print-method jobs predate
// `source`, so a job without one is a print-method job.
const NECK = "neck_labels";
const DST = "dst_replace";
const PLACEMENT = "placement_duplicate";
const apiFor = (job) =>
  job?.source === PLACEMENT
    ? {
        get: getPlacementJob,
        cancel: cancelPlacementJob,
        retryFailed: retryPlacementJob,
        dismiss: dismissPlacementJob,
        undo: undoPlacementJob,
      }
    : job?.source === DST
    ? {
        get: getDstReplaceJob,
        cancel: cancelDstReplaceJob,
        retryFailed: retryFailedDstReplaceJob,
        dismiss: dismissDstReplaceJob,
      }
    : job?.source === NECK
    ? {
        get: getNeckLabelJob,
        cancel: cancelNeckLabelJob,
        retryFailed: retryFailedNeckLabelJob,
        dismiss: dismissNeckLabelJob,
      }
    : {
        get: getBulkJob,
        cancel: cancelBulkJob,
        retryFailed: retryFailedBulkJob,
        dismiss: dismissBulkJob,
      };
const jobById = (jobId) => state.jobs.find((j) => j.jobId === jobId);

const setState = (patch) => {
  state = { ...state, ...patch };
  emit();
};

const sortJobs = (jobs) =>
  [...jobs].sort((a, b) => (b?.createdAt || 0) - (a?.createdAt || 0));

const upsertJob = (job) => {
  if (!job?.jobId) return;
  const prev = state.jobs.find((j) => j.jobId === job.jobId);
  const others = state.jobs.filter((j) => j.jobId !== job.jobId);
  setState({ jobs: sortJobs([...others, { ...prev, ...job }]) });
  if (prev && isJobRunning(prev) && !isJobRunning(job)) {
    finishListeners.forEach((fn) => {
      try {
        fn(job);
      } catch (_) {
        /* a listener must not break polling */
      }
    });
  }
};

const removeJob = (jobId) =>
  setState({ jobs: state.jobs.filter((j) => j.jobId !== jobId) });

const schedulePoll = (ms = POLL_MS) => {
  if (pollTimer) clearTimeout(pollTimer);
  pollTimer = null;
  if (!state.jobs.some(isJobRunning)) return;
  pollTimer = setTimeout(pollRunning, ms);
};

async function pollRunning() {
  pollTimer = null;
  const gen = generation;
  const running = state.jobs.filter(isJobRunning);
  if (!running.length) return;
  // Each job on its own: one missing job must not stall or drop the others.
  const results = await Promise.allSettled(
    running.map((job) =>
      apiFor(job)
        .get(job.jobId)
        .then((res) => (res?.data ? { source: job.source, ...res.data } : res?.data))
    )
  );
  if (gen !== generation) return;
  let anyError = false;
  results.forEach((r, i) => {
    if (r.status === "fulfilled") {
      if (r.value) upsertJob(r.value);
      return;
    }
    const code = r.reason?.response?.status;
    if (code === 404 || code === 403) removeJob(running[i].jobId);
    else anyError = true;
  });
  errorStreak = anyError ? errorStreak + 1 : 0;
  schedulePoll(errorStreak > 3 ? POLL_BACKOFF_MS : POLL_MS);
}

export const bulkJobsStore = {
  subscribe(fn) {
    listeners.add(fn);
    return () => listeners.delete(fn);
  },
  getSnapshot() {
    return state;
  },

  /**
   * Load every job the tracker should show for this account. Safe to call
   * repeatedly; a different uid starts from an empty list.
   */
  async load(uid = null, { isAdmin = false } = {}) {
    if (uid !== currentUid) {
      currentUid = uid;
      generation += 1;
      if (pollTimer) clearTimeout(pollTimer);
      pollTimer = null;
      setState({ jobs: [], loaded: false });
    }
    const gen = generation;
    try {
      // One source failing must not hide the other's jobs.
      const [blank, neck, dst, placement] = await Promise.allSettled([
        isAdmin ? listActiveBulkJobs() : Promise.resolve(null),
        listActiveNeckLabelJobs(),
        listActiveDstReplaceJobs(),
        isAdmin ? listActivePlacementJobs() : Promise.resolve(null),
      ]);
      if (gen !== generation) return;
      const jobs = [
        ...(blank.status === "fulfilled" ? blank.value?.data?.jobs || [] : []),
        ...(neck.status === "fulfilled"
          ? (neck.value?.data?.jobs || []).map((job) => ({ ...job, source: NECK }))
          : []),
        ...(dst.status === "fulfilled"
          ? (dst.value?.data?.jobs || []).map((job) => ({ ...job, source: DST }))
          : []),
        ...(placement.status === "fulfilled"
          ? (placement.value?.data?.jobs || []).map((job) => ({ ...job, source: PLACEMENT }))
          : []),
      ];
      // Merge: a job we were already polling that has since ended fires the
      // finish listeners through upsertJob.
      jobs.forEach(upsertJob);
      setState({ loaded: true });
      schedulePoll(0);
    } catch (_) {
      if (gen === generation) setState({ loaded: true });
    }
  },

  /** A job the client just started. */
  add(job) {
    upsertJob(job);
    setState({ minimized: false });
    schedulePoll(POLL_MS);
  },

  async cancel(jobId) {
    const job = jobById(jobId);
    const res = await apiFor(job).cancel(jobId);
    if (res?.data) upsertJob({ source: job?.source, ...res.data });
    schedulePoll(0);
  },

  async retryFailed(jobId) {
    const job = jobById(jobId);
    const res = await apiFor(job).retryFailed(jobId);
    removeJob(jobId);
    if (res?.data) bulkJobsStore.add({ source: job?.source, ...res.data });
    return res?.data;
  },

  /** Placement jobs only: reverse a finished run. */
  async undo(jobId) {
    const job = jobById(jobId);
    const api = apiFor(job);
    if (!api.undo) return null;
    const res = await api.undo(jobId);
    if (res?.data) bulkJobsStore.add({ source: job?.source, ...res.data });
    return res?.data;
  },

  async dismiss(jobId) {
    const job = jobById(jobId);
    removeJob(jobId);
    try {
      await apiFor(job).dismiss(jobId);
    } catch (_) {
      /* hidden locally either way; the server copy expires from view on reload */
    }
  },

  setMinimized(minimized) {
    try {
      window.sessionStorage.setItem(MINIMIZED_KEY, minimized ? "1" : "0");
    } catch (_) {
      /* ignore */
    }
    setState({ minimized });
  },

  /** Called with the job when a running job reaches a final state. */
  onJobFinished(fn) {
    finishListeners.add(fn);
    return () => finishListeners.delete(fn);
  },

  stop() {
    generation += 1;
    if (pollTimer) clearTimeout(pollTimer);
    pollTimer = null;
  },
};

export const useBulkJobs = () =>
  useSyncExternalStore(bulkJobsStore.subscribe, bulkJobsStore.getSnapshot);
